import React, { useState } from 'react'

import { F0Box, F0Button, F0Heading, F0Text } from '@factorialco/f0-react'
import {
  WizardStep as DeprecatedWizardStep,
  WizardStepRenderParams as DeprecatedWizardStepRenderParams,
  useOnNext,
} from '@factorialco/deprecated-design-system'
import {
  IntegrationsNetsuiteCreateKeyPairDocument,
  ItemIntegrationFragment,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import TextField from 'modules/core/components/TextField'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import asyncAction from 'modules/core/lib/asyncAction'
import { downloadBlob } from 'modules/core/lib/download'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { NetsuiteWizardData } from 'modules/integrations_netsuite/lib/types'
// eslint-disable-next-line factorial/no-allowed-mobx-collections -- Mirroring the existing SettingsWizard pattern; partial installation creation lives in the same mobx-rest store
import { useCompanyInstallations } from 'modules/marketplace/store/collections/companyInstallations'

type NetSuiteWizardStep = DeprecatedWizardStep<NetsuiteWizardData>
type NetSuiteWizardStepRenderParams = DeprecatedWizardStepRenderParams<NetsuiteWizardData>

interface Props extends NetSuiteWizardStepRenderParams {
  integration: ItemIntegrationFragment
}

const GuideCard = ({
  step,
  title,
  description,
}: {
  step: string
  title: string
  description: string
}) => (
  <F0Box
    display="flex"
    flexDirection="row"
    gap="md"
    padding="lg"
    border="default"
    borderColor="secondary"
    borderRadius="md"
    background="primary"
  >
    <F0Box
      width="8"
      height="8"
      display="flex"
      alignItems="center"
      justifyContent="center"
      borderRadius="xl"
      background="info"
    >
      <F0Text variant="label" content={step} />
    </F0Box>
    <F0Box display="flex" flexDirection="column" gap="xs">
      <F0Text variant="label" content={title} />
      <F0Text variant="body" color="secondary" content={description} />
    </F0Box>
  </F0Box>
)

const GenerateCertificateStep = ({ data, onDataChanged, integration }: Props) => {
  const session = useCurrentSession()
  const companyInstallations = useCompanyInstallations()
  const [generating, setGenerating] = useState(false)

  const [createKeyPair] = useMutation(IntegrationsNetsuiteCreateKeyPairDocument, {
    extractorFn: (responseData) =>
      responseData.integrationsNetsuiteMutations.createIntegrationsnetsuiteKeyPair,
  })

  useOnNext(() => {
    if (data.answers.certificate_id) {
      track(trackingEvents.integrationsNetsuite.netsuiteSettingsCertificateIdEntered, {
        company_id: session.company.id,
        access_id: session.access.id,
      })
    }
  })

  // certificate_id persists in formData; if present we are revisiting the step.
  const hasExistingCertificate = !!data.answers.certificate_id
  const hasGeneratedCertificate = !!data.answers.certificate || !!data.answers.private_key
  // True only after the user has just generated a new keypair in the current session.
  const justGenerated = !!data.certificateGenerated

  const ensureInstallation = async (): Promise<boolean> => {
    if (data.installationCreated) return true

    await companyInstallations.fetch()
    const existingInstallation = companyInstallations.find({ integration_id: integration.id })
    if (existingInstallation) {
      onDataChanged({
        ...data,
        installationCreated: true,
      })
      return true
    }

    const payload = {
      access_id: session.access.id,
      form_data: {
        account_id: data.answers.account_id,
        client_id: data.answers.client_id,
      },
      company_id: session.company.id,
      integration_id: integration.id,
    }
    const { success } = await asyncAction(async () => {
      await companyInstallations.create(payload)
    })
    if (success) {
      onDataChanged({
        ...data,
        installationCreated: true,
      })
      return true
    }

    // If another request already created the partial installation, the create
    // path returns 409. Refetch and continue when the installation now exists.
    await companyInstallations.fetch()
    const existingAfterCreate = companyInstallations.find({ integration_id: integration.id })
    if (existingAfterCreate) {
      onDataChanged({
        ...data,
        installationCreated: true,
      })
      return true
    }

    return false
  }

  const handleGenerate = async () => {
    setGenerating(true)
    try {
      const installationOk = await ensureInstallation()
      if (!installationOk) {
        app.setFlash({ type: 'wrong', message: i18n.t('common.error') })
        return
      }

      const result = await createKeyPair({
        variables: { companyId: session.company.id },
      })
      const handled = handleResult(result, () => undefined)
      if (!handled.success || !handled.data?.keyPair) {
        app.setFlash({ type: 'wrong', message: i18n.t('common.error') })
        return
      }

      const certificate = handled.data.keyPair.certificate
      const pemBlob = new Blob([certificate], { type: 'application/x-pem-file' })
      downloadBlob(pemBlob, 'factorial-netsuite-certificate.pem')

      // When regenerating, clear the previous certificate_id: the user must paste
      // the new one returned by NetSuite after uploading the new public cert.
      onDataChanged({
        ...data,
        installationCreated: true,
        certificateGenerated: true,
        answers: {
          ...data.answers,
          certificate_id: '',
        },
      })

      track(trackingEvents.integrationsNetsuite.netsuiteSettingsCertificateGenerated, {
        company_id: session.company.id,
        access_id: session.access.id,
      })
    } finally {
      setGenerating(false)
    }
  }

  const handleCertificateIdChange = (value: string) => {
    onDataChanged({
      ...data,
      answers: {
        ...data.answers,
        certificate_id: value,
      },
    })
  }

  // Show the upload + certificate_id input section whenever the user can paste
  // a value: either there is already a saved one, or they just generated a new
  // keypair in this session or in a previous visit.
  const showCertificateInput = hasExistingCertificate || hasGeneratedCertificate || justGenerated
  const generateButtonLabel =
    hasExistingCertificate || justGenerated
      ? i18n.t(
          'app_store_v2.app_integrations.integrations_netsuite.settings.steps.generate_certificate.regenerate_button'
        )
      : i18n.t(
          'app_store_v2.app_integrations.integrations_netsuite.settings.steps.generate_certificate.generate_button'
        )

  return (
    <F0Box display="flex" flexDirection="row" gap="2xl">
      <F0Box display="flex" flexDirection="column" gap="xl" maxWidth="screen-sm">
        <F0Box display="flex" flexDirection="column" gap="xs">
          <F0Heading
            as="h2"
            variant="heading"
            content={i18n.t(
              'app_store_v2.app_integrations.integrations_netsuite.settings.steps.generate_certificate.title'
            )}
          />
          <F0Text
            variant="body"
            color="secondary"
            content={i18n.t(
              'app_store_v2.app_integrations.integrations_netsuite.settings.steps.generate_certificate.description'
            )}
          />
        </F0Box>

        {hasExistingCertificate && !justGenerated && (
          <F0Box display="flex" flexDirection="column" gap="xs">
            <F0Text
              variant="label"
              content={i18n.t(
                'app_store_v2.app_integrations.integrations_netsuite.settings.steps.generate_certificate.existing_certificate_title'
              )}
            />
            <F0Text
              variant="body"
              content={i18n.t(
                'app_store_v2.app_integrations.integrations_netsuite.settings.steps.generate_certificate.existing_certificate_description',
                { certificate_id: data.answers.certificate_id }
              )}
            />
          </F0Box>
        )}

        <F0Box display="flex" flexDirection="column" gap="md">
          <F0Text
            variant="body"
            content={i18n.t(
              'app_store_v2.app_integrations.integrations_netsuite.settings.steps.generate_certificate.instructions'
            )}
          />
          <F0Box display="flex" flexDirection="row">
            <F0Button label={generateButtonLabel} onClick={handleGenerate} disabled={generating} />
          </F0Box>
        </F0Box>

        {showCertificateInput && (
          <F0Box display="flex" flexDirection="column" gap="md">
            <F0Text
              variant="body"
              content={i18n.t(
                'app_store_v2.app_integrations.integrations_netsuite.settings.steps.generate_certificate.upload_instructions'
              )}
            />
            <TextField
              label={i18n.t(
                'app_store_v2.app_integrations.integrations_netsuite.settings.steps.generate_certificate.certificate_id_label'
              )}
              value={data.answers.certificate_id || ''}
              onChange={handleCertificateIdChange}
            />
          </F0Box>
        )}
      </F0Box>

      <F0Box width="96">
        <F0Box
          display="flex"
          flexDirection="column"
          gap="lg"
          padding="lg"
          background="secondary"
          borderRadius="md"
        >
          <F0Text
            variant="label"
            content={i18n.t(
              'app_store_v2.app_integrations.integrations_netsuite.settings.steps.generate_certificate.guide_title'
            )}
          />
          <GuideCard
            step="1"
            title={i18n.t(
              'app_store_v2.app_integrations.integrations_netsuite.settings.steps.generate_certificate.upload_title'
            )}
            description={i18n.t(
              'app_store_v2.app_integrations.integrations_netsuite.settings.steps.generate_certificate.upload_description'
            )}
          />
          <GuideCard
            step="2"
            title={i18n.t(
              'app_store_v2.app_integrations.integrations_netsuite.settings.steps.generate_certificate.role_title'
            )}
            description={i18n.t(
              'app_store_v2.app_integrations.integrations_netsuite.settings.steps.generate_certificate.role_description'
            )}
          />
          <GuideCard
            step="3"
            title={i18n.t(
              'app_store_v2.app_integrations.integrations_netsuite.settings.steps.generate_certificate.copy_certificate_id_title'
            )}
            description={i18n.t(
              'app_store_v2.app_integrations.integrations_netsuite.settings.steps.generate_certificate.copy_certificate_id_description'
            )}
          />
          <F0Box
            display="flex"
            flexDirection="column"
            gap="xs"
            padding="lg"
            border="default"
            borderColor="warning"
            borderRadius="md"
            background="warning"
          >
            <F0Text
              variant="label"
              content={i18n.t(
                'app_store_v2.app_integrations.integrations_netsuite.settings.steps.generate_certificate.missing_option_title'
              )}
            />
            <F0Text
              variant="body"
              content={i18n.t(
                'app_store_v2.app_integrations.integrations_netsuite.settings.steps.generate_certificate.missing_option_description'
              )}
            />
          </F0Box>
        </F0Box>
      </F0Box>
    </F0Box>
  )
}

export const generateCertificateStep =
  (integration: ItemIntegrationFragment) => (): NetSuiteWizardStep => ({
    title: i18n.t(
      'app_store_v2.app_integrations.integrations_netsuite.settings.steps.generate_certificate.wizard_title'
    ),
    nextText: i18n.t('common.next'),
    render: (props) => <GenerateCertificateStep {...props} integration={integration} />,
    isCompleted: (data) => !!data.answers.certificate_id,
  })
